import React from 'react';
import { MediaItem, SpecialEffect } from '../../types'; // adjust the path as needed
import { updateMediaWithScreenshots } from './mediaItemService';
import axios from 'axios';
import { getApiUrl } from '../../config/api';

export async function autoGenerateLibraryMedia(
  projectId: string,
  audioRef: React.RefObject<HTMLAudioElement>,
  setMedia: React.Dispatch<React.SetStateAction<MediaItem[]>>,
  token?: string
): Promise<MediaItem[]|null> {
  try {
    if (!audioRef.current) {
      throw new Error("Audio element is not available");
    }
    
    console.log("---> Auto-generating media from library...");
    
    // Show loading state
    setMedia([{
      id: 'loading',
      name: 'Loading media from library...',
      start: 0,
      end: 5,
      url: '',
      type: 'image',
      length: 5,
      src: '',
      effect: SpecialEffect.NONE
    }]);
    
    // Get the duration of the audio from the audio ref
    const audioLength = audioRef.current.duration;
    
    // Construct the API URL with the query parameters
    const url = getApiUrl(`/media/generate-media-from-library?audioLength=${audioLength}&projectId=${projectId}`);
    
    // Fetch the media from the library
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        // Add authorization header if token exists
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
    });
    if (!response.ok) {
      throw new Error(`HTTP error: ${response.status}`);
    }
    
    const libraryData: MediaItem[] = await response.json();
    console.log(`Received ${libraryData.length} items from library API`);
    
    // Process the media items - using same URL for both src and url fields
    console.log("---> Processing media items (using source URLs directly)");
    const processedItems = await updateMediaWithScreenshots(libraryData, setMedia);
    
    console.log("---> Auto-generated media completed with", processedItems.length, "items");
    return processedItems;
  } catch (error) {
    console.error("Error auto-generating media from library:", error);
    // Show error in the media
    setMedia([{
      id: 'error',
      name: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
      start: 0,
      end: 5,
      url: '',
      type: 'image',
      length: 5,
      src: '',
      effect: SpecialEffect.NONE
    }]);
    return null;
  }
}


interface FileUploadResponse {
  files: Array<{
    url: string;
    name: string;
  }>;
}

export const uploadLibraryMedia = async (
  files: File[], 
  projectId: string, 
  isAStarter: boolean = false,
  token?: string
): Promise<FileUploadResponse> => {
  // Create a FormData object to send files
  const formData = new FormData();
  
  // Append project ID and starter flag
  formData.append('projectId', projectId);
  formData.append('isAStarter', isAStarter.toString());
  
  // Append each file
  files.forEach((file, index) => {
    formData.append('files', file);
  });

  try {
    // Validate files before upload
    const allowedTypes = [
      'image/jpeg', 
      'image/png', 
      'image/gif', 
      'image/webp',
      'video/mp4', 
      'video/mpeg', 
      'video/quicktime', 
      'video/x-msvideo'
    ];

    // Check if all files are of allowed types
    const invalidFiles = files.filter(file => !allowedTypes.includes(file.type));
    
    if (invalidFiles.length > 0) {
      throw new Error('Some files are not of allowed types. Only images and videos are permitted.');
    }

    // Max file size check (optional - adjust as needed)
    const MAX_FILE_SIZE = 10000 * 1024 * 1024; // 10GB
    const oversizedFiles = files.filter(file => file.size > MAX_FILE_SIZE);
    
    if (oversizedFiles.length > 0) {
      throw new Error(`Some files exceed the maximum upload size of ${MAX_FILE_SIZE / (1024 * 1024)}MB.`);
    }

    // Perform the upload
    const response = await axios.post<FileUploadResponse>(getApiUrl('/media/library-upload'), formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        // Add authorization header if token exists
        ...(token && { 'Authorization': `Bearer ${token}` }),
      }
    });

    return response.data;

  } catch (error) {
    // Handle different types of errors
    if (axios.isAxiosError(error)) {
      // Axios-specific error handling
      console.error('Upload failed:', error.response?.data || error.message);
      throw new Error(error.response?.data?.message || 'File upload failed');
    } else if (error instanceof Error) {
      // Other errors (like validation errors)
      console.error('Upload error:', error.message);
      throw error;
    } else {
      // Unexpected error
      console.error('An unexpected error occurred during upload');
      throw new Error('An unexpected error occurred during upload');
    }
  }
};

